import { DateTime } from "luxon";
import type { MRT_ColumnDef } from "mantine-react-table-open";
import { luxonDateTimeToBackendDateOrDateTime } from "../../../../utils/dates/luxonDateTimeToBackendDateOrDateTime";
import { IconHelpText } from "../../../IconHelpText/IconHelpText";
import type { ChangeableFieldData } from "../../ChangeableFieldWidget";
import { calculateFirstPossibleStartDate } from "../../utils/calculateFirstPossibleStartDate";
import { DatePickerCell } from "../Cells/DatePickerCell";
import type { ChangeableFieldTableOptions } from "../ChangeableFieldTable";

function getStartDateColumn<T>(
  values: Array<ChangeableFieldData<T>>,
  handleChangeStartDate: (
    index: number,
    newStartDate: DateTime<boolean> | null
  ) => void,
  tableOptions: ChangeableFieldTableOptions = {}
): MRT_ColumnDef<ChangeableFieldData<T>> {
  return {
    header: "Von",
    Header: () => (
      <span>
        Von
        {tableOptions.startDateIsRequired && "*"}
        {!!tableOptions.startDateHelpText && (
          <>
            {" "}
            <IconHelpText helpText={tableOptions.startDateHelpText} />
          </>
        )}
      </span>
    ),
    accessorKey: "startDate",
    size: tableOptions.dateCellWidth ? tableOptions.dateCellWidth : 110,
    Cell: ({ row, cell }) => (
      <StartDateCell
        endDate={row.original.endDate}
        isFirstDate={row.index === 0}
        placeholder={tableOptions.startDatePlaceholder}
        previousRowStartDate={
          row.index > 0 ? values[row.index - 1].startDate : undefined
        }
        restrictFutureDates={tableOptions.restrictFutureDates}
        startDate={cell.getValue<DateTime | null>()}
        onChange={(newStartDate) =>
          handleChangeStartDate(row.index, newStartDate)
        }
      />
    )
  };
}

interface StartDateCellProps {
  startDate: DateTime | null;
  endDate: DateTime | null;
  previousRowStartDate: DateTime | null | undefined;
  isFirstDate: boolean;
  placeholder?: string;
  restrictFutureDates?: boolean;
  onChange: (newEndDate: DateTime | null) => void;
}

function StartDateCell({
  startDate,
  endDate,
  previousRowStartDate,
  isFirstDate,
  placeholder,
  restrictFutureDates,
  onChange
}: StartDateCellProps) {
  const firstPossibleDate =
    calculateFirstPossibleStartDate(previousRowStartDate);
  const lastPossibleDate = restrictFutureDates
    ? endDate
      ? endDate
      : DateTime.now()
    : undefined;

  if (!startDate) {
    return <span>{placeholder}</span>;
  }

  if (isFirstDate) {
    return <span>{luxonDateTimeToBackendDateOrDateTime(startDate)}</span>;
  }

  return (
    <DatePickerCell
      firstPossibleDate={firstPossibleDate}
      lastPossibleDate={lastPossibleDate}
      value={startDate}
      onChange={onChange}
    />
  );
}

export { getStartDateColumn };
